<template>
  <div>
    <AAlert
      v-if="errorMessage"
      type="error"
      :message="errorMessage"
      banner
      closable
      @close="errorMessage = null"
    />

    <!-- filter -->
    <ARow :gutter="[8, 8]">
      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <FilterATSalesmanSalesman
          style="width:100%;"
          :mode="null"
          v-model:value="state.params.salesman"
          v-model:useram_id="state.useram_id"
          @change="onSalesmanChange"
          placeholder="Pilih Salesman"
        />
      </ACol>
      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <FilterToko
          style="width:100%;"
          v-model:value="state.params.toko"
          placeholder="Pilih Toko"
        />
      </ACol>
      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <FilterKabupaten
          style="width:100%;"
          v-model:value="state.params.opco"
          placeholder="Pilih Kabupaten/Kota"
        />
      </ACol>
      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <FilterATSalesmanUserAMPeriode
          style="width:100%;"
          v-model:value="state.params.tso"
          :periode="state.params.bulan"
          placeholder="Pilih TSO"
        />
      </ACol>
      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <AMonthPicker
          v-model:value="state.params.bulan"
          format="YYYY-MM"
          value-format="YYYY-MM"
          placeholder="Pilih Bulan"
          style="width:100%;"
          :allow-clear="false"
        />
      </ACol>

      <ACol :span="24">
        <AButton
          type="primary"
          title="cari"
          @click="fetchData"
          :loading="state.isFetching"
        >
          <span v-if="!state.isFetching" class="fa fa-search" aria-hidden="true" />
          <span v-else>Memuat Data ...</span>
        </AButton>
      </ACol>
    </ARow>

    <!-- info box -->
    <div v-if="state.bulanLabel" class="info-box mt-4">
      <div class="info-item">
        <span class="info-label">Salesman</span>
        <span class="info-value">
          {{ state.params.salesman && state.salesmanLabel
              ? `${state.params.salesman} - ${state.salesmanLabel}`
              : state.salesmanLabel ?? '-' }}
        </span>
      </div>
      <div class="info-item">
        <span class="info-label">Distributor</span>
        <span class="info-value">
          <span v-if="state.distributors.length">
            <span v-for="(d, i) in state.distributors" :key="d.vendor_id">
              {{ d.vendor_code }} - {{ d.vendor_name }}<span v-if="i < state.distributors.length - 1">, </span>
            </span>
          </span>
          <span v-else>-</span>
        </span>
      </div>
      <div class="info-item">
        <span class="info-label">Bulan</span>
        <span class="info-value">{{ state.bulanLabel }}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Total Toko</span>
        <span class="info-value">{{ state.totalToko }}</span>
      </div>
    </div>

    <!-- header atas tabel -->
    <div v-if="state.bulanLabel" class="table-header mt-4">
      <span class="table-title">Data Tracking Toko</span>
      <AButton
        type="primary"
        title="Export Excel"
        @click="exportExcel"
        :loading="state.isExporting"
        :disabled="!state.bulanLabel"
      >
        <span v-if="!state.isExporting">
          <span class="fa fa-file-excel-o" aria-hidden="true" />
          &nbsp;Export
        </span>
        <span v-else>Mengunduh ...</span>
      </AButton>
    </div>

    <!-- tabel -->
    <div class="table-responsive text-nowrap" :class="state.bulanLabel ? '' : 'mt-4'">
      <MdTable
        :columns="dynamicColumns"
        :data-source="state.data"
        :loading="state.isFetching"
        :pagination="false"
        size="small"
        :row-key="(record, index) => record.id_toko ?? `total-${index}`"
        :scroll="{ x: 'max-content' }"
        :row-class-name="(record) => !record.id_toko ? 'font-weight-bold bg-light' : ''"
      >
        <template
          v-for="day in state.jumlahHari"
          :key="`slot_d${day}`"
          #[`d${day}`]="{ text }"
        >
          <span v-if="text === null || text === undefined">-</span>
          <span v-else-if="Number(text) > 0" class="cell-positive">
            {{ Number(text).toFixed(2) }}
          </span>
          <span v-else class="cell-zero">0</span>
        </template>

        <template #total_volume_ton="{ text }">
          <span class="cell-bold">
            {{ Number(text) > 0 ? Number(text).toFixed(2) : '-' }}
          </span>
        </template>

        <template #freq_visit="{ text }">
          {{ text ?? 0 }}
        </template>
      </MdTable>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import apiClient from '@/services/axios'
import FilterATSalesmanSalesman from '@/components/filter/FilterATSalesmanSalesman'
import FilterToko from '@/components/filter/FilterToko'
import FilterKabupaten from '@/components/filter/FilterKabupaten'
import FilterATSalesmanUserAMPeriode from '@/components/filter/FilterATSalesmanUserAMPeriode'
import localStorage from 'store'
import { hasRoles, ROLE_TSO, ROLE_DISTRIBUTOR } from '@/helpers'

const NAMA_BULAN = [
  '', 'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
]

const STATIC_COLUMNS = [
  { title: 'No',        dataIndex: 'no',            width: 52,  fixed: 'left', align: 'center' },
  { title: 'ID Toko',   dataIndex: 'id_toko',       width: 100, fixed: 'left', align: 'center' },
  { title: 'Nama Toko', dataIndex: 'retailer_name', width: 180, ellipsis: true },
  { title: 'Kabupaten', dataIndex: 'kabupaten',     width: 140, ellipsis: true },
  { title: 'TSO',       dataIndex: 'tso_name',      width: 150, ellipsis: true },
  { title: 'Cluster',   dataIndex: 'cluster',       width: 110, ellipsis: true },
  {
    title: 'Target (TON)',
    dataIndex: 'target_ton',
    width: 110,
    align: 'right',
    customRender: () => '-',
  },
  {
    title: 'Freq Visit',
    dataIndex: 'freq_visit',
    width: 90,
    align: 'center',
    slots: { customRender: 'freq_visit' },
  },
]

const TOTAL_COLUMN = {
  title:     'Total (TON)',
  dataIndex: 'total_volume_ton',
  width:     110,
  fixed:     'right',
  align:     'right',
  slots:     { customRender: 'total_volume_ton' },
}

function formatYearMonth(date) {
  const year  = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}

function toBulanLabel(bulan) {
  if (!bulan) return null
  const [year, month] = bulan.split('-')
  return `${NAMA_BULAN[parseInt(month)]} ${year}`
}

function buildDayColumns(jumlahHari) {
  return Array.from({ length: jumlahHari }, (_, i) => ({
    title:     `${i + 1}`,
    dataIndex: `d${i + 1}`,
    width:     58,
    align:     'right',
    slots:     { customRender: `d${i + 1}` },
  }))
}

export default defineComponent({
  name: 'TrackingTokoPage',

  components: {
    FilterATSalesmanSalesman,
    FilterToko,
    FilterKabupaten,
    FilterATSalesmanUserAMPeriode,
  },

  setup() {
    const route        = useRoute()
    const errorMessage = ref(null)
    const user         = computed(() => localStorage.get('profile'))

    const state = reactive({
      isFetching:    false,
      isExporting:   false,
      data:          [],
      jumlahHari:    0,
      bulanLabel:    null,
      salesmanLabel: null,
      totalToko:     0,
      distributors:  [],

      params: {
        salesman: route.params.id ? Number(route.params.id) : null,
        toko:     [],
        opco:     [],
        tso:      [],
        bulan:    formatYearMonth(new Date()),
      },

      useram_id: null,
    })

    const dynamicColumns = computed(() => [
      ...STATIC_COLUMNS,
      ...buildDayColumns(state.jumlahHari),
      TOTAL_COLUMN,
    ])

    const buildParams = () => ({
      bulan: state.params.bulan,
      ...(state.params.toko?.length && { toko: state.params.toko }),
      ...(state.params.opco?.length && { opco: state.params.opco }),
      ...(state.params.tso?.length  && { tso:  state.params.tso  }),
    })

    const onSalesmanChange = (val, item) => {
      if (val && item) state.salesmanLabel = item.name
      else state.salesmanLabel = null
    }

    const fetchData = () => {
      if (!state.params.salesman) {
        errorMessage.value = 'Pilih salesman terlebih dahulu.'
        return
      }

      state.isFetching   = true
      errorMessage.value = null

      apiClient
        .get(`/api/users-salesman/tracking/${state.params.salesman}`, { params: buildParams() })
        .then(({ data }) => {
          state.data         = data.data         ?? []
          state.jumlahHari   = data.jumlah_hari  ?? 0
          state.bulanLabel   = toBulanLabel(data.bulan)
          state.totalToko    = state.data.filter(row => row.id_toko).length
          state.distributors = data.distributors ?? []
        })
        .catch(() => {
          errorMessage.value = 'Gagal memuat data tracking toko.'
        })
        .finally(() => {
          state.isFetching = false
        })
    }

    const exportExcel = () => {
      if (!state.params.salesman) {
        errorMessage.value = 'Pilih salesman terlebih dahulu.'
        return
      }

      state.isExporting  = true
      errorMessage.value = null

      const params = { ...buildParams(), _export: 'xls' }

      apiClient
        .get(`/api/users-salesman/tracking/${state.params.salesman}`, {
          params,
          responseType: 'blob',
        })
        .then(({ data, headers }) => {
          let filename = `tracking-toko-${state.params.salesman}-${state.params.bulan}.xlsx`
          const disposition = headers['content-disposition']
          if (disposition) {
            const match = disposition.match(/filename="?([^";\n]+)"?/)
            if (match) filename = match[1]
          }
          const url  = window.URL.createObjectURL(new Blob([data]))
          const link = document.createElement('a')
          link.href  = url
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
        })
        .catch(() => {
          errorMessage.value = 'Gagal mengunduh file Excel.'
        })
        .finally(() => {
          state.isExporting = false
        })
    }

    onMounted(() => {
      state.params.toko = []
      state.params.opco = []
      state.params.tso  = []
      if (hasRoles([ROLE_TSO])) state.useram_id = user.value.id

      if (state.params.salesman) {
        fetchData()
      }
    })

    return {
      errorMessage,
      state,
      dynamicColumns,
      fetchData,
      exportExcel,
      onSalesmanChange,
      hasRoles,
      ROLE_TSO,
    }
  },
})
</script>

<style scoped>
.cell-positive {
  color: #1890ff;
  font-weight: bold;
}
.cell-zero {
  color: #bfbfbf;
}
.cell-bold {
  font-weight: bold;
}

.info-box {
  display: inline-flex;
  flex-direction: column;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  overflow: hidden;
  min-width: 320px;
}
.info-item {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
}
.info-item:last-child {
  border-bottom: none;
}
.info-label {
  width: 100px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
  flex-shrink: 0;
}
.info-value {
  color: rgba(0, 0, 0, 0.85);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 0;
}
.table-title {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}
</style>
